import * as React from 'react';

import type { Extension } from '@codemirror/state';
import { CodeEditor, ExpressionVariable, cn } from '@rematter/ui-web';
import type { JSONSchema7 } from 'json-schema';

import { useAutomationLspContext, getExpressionDocumentUri } from '../_lsp';

type ExpressionInputProps = {
  value: string;
  onChange: (value: string) => void;
  /** JSON Schema describing available variables for autocomplete (fallback when LSP not available) */
  environmentSchema?: JSONSchema7;
  /** Unique identifier for this node (used for LSP document URI) */
  nodeId?: string;
  /** Field name for this expression (used for multiple expressions per node) */
  fieldName?: string;
  label?: string;
  placeholder?: string;
  description?: string;
  className?: string;
  errorMessage?: string;
  height?: string;
  multiline?: boolean;
  /** Force disable LSP and use legacy completion (default: false) */
  disableLsp?: boolean;
};

/**
 * Flatten JSON Schema into ExpressionVariable[] for autocompletion.
 */
function schemaToVariables(
  schema: JSONSchema7,
  prefix = '',
): ExpressionVariable[] {
  const results: ExpressionVariable[] = [];

  if (schema.type === 'object' && schema.properties) {
    for (const [key, prop] of Object.entries(schema.properties)) {
      if (typeof prop === 'boolean') continue;

      const path = prefix ? `${prefix}.${key}` : key;
      const propType = Array.isArray(prop.type)
        ? prop.type.join(' | ')
        : prop.type || 'any';

      results.push({
        path,
        label: prop.title || key,
        type: propType,
      });

      // Recurse into nested objects
      if (prop.type === 'object' && prop.properties) {
        results.push(...schemaToVariables(prop, path));
      }

      // Handle arrays with object items
      if (
        prop.type === 'array' &&
        prop.items &&
        typeof prop.items !== 'boolean'
      ) {
        const items = prop.items as JSONSchema7;
        if (items.type === 'object' && items.properties) {
          results.push(...schemaToVariables(items, `${path}[0]`));
        }
      }
    }
  }

  return results;
}

/**
 * Expression input using CodeMirror with expr-lang syntax.
 * Uses expr-lang syntax (not {{}} interpolation).
 *
 * When used within an LspProvider and given a nodeId, uses the LSP server
 * for intelligent autocomplete, hover tooltips, and diagnostics.
 * Falls back to schema-based completion when LSP is not available.
 */
export const ExpressionInput: React.FC<ExpressionInputProps> = ({
  value,
  onChange,
  environmentSchema,
  nodeId,
  fieldName,
  label,
  placeholder,
  description,
  className,
  errorMessage,
  height,
  multiline = false,
  disableLsp = false,
}) => {
  const lspContext = useAutomationLspContext();

  // Determine if we should use LSP
  const shouldUseLsp = !disableLsp && nodeId && lspContext !== null;
  const lspReady = shouldUseLsp && lspContext?.isReady;

  // Get LSP extensions when available
  // IMPORTANT: We must get extensions BEFORE the editor mounts,
  // otherwise the LSP plugin won't send textDocument/didOpen
  const lspExtensions = React.useMemo<Extension | undefined>(() => {
    if (!lspReady || !lspContext || !nodeId) {
      return undefined;
    }
    const documentUri = getExpressionDocumentUri(nodeId, fieldName);
    // Pass 'expr' as the languageId to ensure proper LSP communication
    return lspContext.getExtensions(documentUri, 'expr');
  }, [lspReady, lspContext, nodeId, fieldName]);

  // Fallback: flatten schema for legacy completion
  const variables = React.useMemo(() => {
    // Skip if using LSP
    if (lspReady) return [];
    return environmentSchema ? schemaToVariables(environmentSchema) : [];
  }, [environmentSchema, lspReady]);

  // If we should use LSP but it's not ready yet, show a loading state
  // This ensures the editor mounts WITH LSP extensions, not without
  if (shouldUseLsp && !lspReady) {
    return (
      <div className={className}>
        {label && (
          <div className="mb-1.5 text-sm font-medium text-neutral">{label}</div>
        )}
        {description && (
          <div className="mb-1.5 text-sm text-neutral/60">{description}</div>
        )}
        <div
          className="flex items-center justify-center rounded-md border border-neutral/20 bg-neutral/5"
          style={{ height: multiline ? height || '120px' : '38px' }}
        >
          <span className="text-xs text-neutral/40">Loading editor...</span>
        </div>
      </div>
    );
  }

  return (
    <CodeEditor
      value={value}
      onChange={onChange}
      language="expr"
      label={label}
      description={description}
      placeholder={placeholder}
      errorMessage={errorMessage}
      isInvalid={!!errorMessage}
      className={cn('@ExpressionInput', className)}
      height={multiline ? height || '120px' : '38px'}
      minHeight={multiline ? '80px' : undefined}
      expressionVariables={variables}
      showLineNumbers={multiline}
      additionalExtensions={lspExtensions}
      disableBuiltinAutocompletion={!!lspReady}
    />
  );
};
